// Pure helpers behind "โปรโมชั่น Shopee → seller" — the page at
// /app-catalog/shopee-promotions and the flash-ownership badge on
// /app-catalog/products/:id/edit (B7 FE-1, spec
// docs/specs/2026-10-02-shopee-promotions-to-seller-design.md §5.1 HTTP + §6 UI
// + §7 edge cases + §10 owner decisions).
//
// Vue-free / axios-free on purpose: this repo's jest cannot mount a .vue file
// (see tests/unit/skuWmsOptionsSource.spec.js and reference-bo-fn-jest-infra),
// so everything decidable — the sync body, every badge, every sentence, the
// Bangkok clock, how a 409 hands back the run that is already going, and which
// `flash_source` a save must carry — lives here where a spec can call it
// directly. ShopeePromotions.vue only renders and sequences.
//
// --------------------------------------------------------------------------
// THE BACKEND CONTRACT (§5.1, BE-1 / asrth/mdf-bo-api#760). Written in parallel
// with the handler, so these bytes are the agreement, not an observation of a
// running server.
//
//   GET /api/platform/v2/shopee/promotions
//       ?platform_shop_id=&type=all|discount|flash_sale
//       &status=all|upcoming|ongoing|expired&page=&page_size=
//   200 {shop_id, fetched_at, mode, more, warnings:[],
//        show_upcoming,                       // owner decision §10.3
//        promotions:[{promotion_type, promotion_id, name, starts_at, ends_at,
//                     remote_status, status:"upcoming|ongoing|expired|unsupported",
//                     item_count,
//                     link:{state:"synced|ended|skipped|failed", sku_count,
//                           last_synced_at, ended_at}}]}
//
//   POST /api/platform/v2/shopee/promotions/sync
//        {platform_shop_id:string, dry_run:bool (default TRUE),
//         promotions?:[{promotion_type, promotion_id}]}
//   200 {shop_id, dry_run, mode, run_id, promotions_total, promotions_applied,
//        skus_set, skus_cleared, skus_skipped, warnings:[],
//        results:[{promotion_type, promotion_id, name, status, starts_at,
//                  ends_at, action:"apply|end|skip|noop", warnings:[],
//                  skus:[{sku_id, sku_code, item_id, model_id, price,
//                         prev_price, purchase_limit,
//                         action:"set|keep|clear|skip", reason_code}]}]}
//
//   GET /api/platform/v2/shopee/promotions/runs?platform_shop_id=&limit=
//   200 {runs:[{id, platform_shop_id, trigger, dry_run, status, promotions_total,
//               promotions_applied, skus_set, skus_cleared, skus_skipped,
//               last_error, started_at, finished_at}],
//        in_flight, mode, enabled, master_shop, show_upcoming}
//       200 EVEN WHEN THE FEATURE IS OFF, with `enabled:false` — which is the
//       only way the page can say "ปิดอยู่" instead of showing an error for a
//       switch that is deliberately down (§9 step 2).
//
//   errors: 400 {code:"shop_required"} · 404 {code:"shop_not_found"}
//         · 409 {code:"promotion_sync_in_flight", run_id}
//         · 422 {code:"bad_promotion_type"} | {code:"shop_not_master"} (§10.5)
//         · 502 {code:"promotion_fetch_failed", message, result}
//         · 503 {code:"promotion_sync_disabled"}
//
// `platform_shop_id` IS A STRING ON EVERY REQUEST (#580: the FE once sent a
// JSON number and every endpoint answered 400). buildSyncBody enforces it.
// --------------------------------------------------------------------------

// --------------------------------------------------------------------------
// vocabularies
// --------------------------------------------------------------------------

/** The two promotion types this feature mirrors (§1: no bundle deal, no voucher). */
export const PROMOTION_TYPE_DISCOUNT = "discount";
export const PROMOTION_TYPE_FLASH_SALE = "flash_sale";
export const PROMOTION_TYPES = [PROMOTION_TYPE_DISCOUNT, PROMOTION_TYPE_FLASH_SALE];

/**
 * The badge states (§6). `unsupported` is NOT a window state: a platform-created
 * discount (`source = 1`, §7 #8) is reported as unsupported whatever its dates
 * say, because the operator's question about it is "why was this not applied".
 */
export const PROMOTION_STATUS_UPCOMING = "upcoming";
export const PROMOTION_STATUS_ONGOING = "ongoing";
export const PROMOTION_STATUS_EXPIRED = "expired";
export const PROMOTION_STATUS_UNSUPPORTED = "unsupported";
export const PROMOTION_STATUSES = [
  PROMOTION_STATUS_UPCOMING,
  PROMOTION_STATUS_ONGOING,
  PROMOTION_STATUS_EXPIRED,
  PROMOTION_STATUS_UNSUPPORTED
];

/** `link.state` — what WE did about one promotion (§3.2 `state`). */
export const APP_STATES = ["synced", "ended", "skipped", "failed"];

/** `results[].action` — the per-promotion outcome of one run (§5.1). */
export const PROMOTION_ACTIONS = ["apply", "end", "skip", "noop"];

/** `results[].skus[].action` — the per-SKU outcome (§5.1). */
export const PROMO_SKU_ACTIONS = ["set", "keep", "clear", "skip"];

/**
 * The reason codes the drawer translates. ONE closed vocabulary shared with the
 * service's constants (services/shopeepromo/shopeepromo.go): a new reason is a
 * new constant there, a new entry here and a new sentence in BOTH dictionaries —
 * never a free-text string rendered raw at an operator.
 *
 * The six of §6 are the ones the spec enumerates; `model_not_active`,
 * `no_promotion_price`, `promotion_ended`, `sku_inactive` and
 * `ownership_columns_missing` are also emitted by BE-1 and are listed so they
 * get a sentence too rather than falling through to the generic one.
 *
 * The last two are #769. `duplicate_sku_mapping` used to be reported as
 * `sku_not_synced`, whose sentence ("ยังไม่ได้ sync สินค้า") sent the operator to
 * the product sync for a problem the product sync cannot fix — the listing IS
 * synced, two of its units just land on one SKU. `remote_expired_window_open` is
 * PROMOTION-level: it carries no sku_code / model_id / item_id, so the drawer
 * renders it as a bare sentence, which the `v-else-if` chain in
 * ShopeePromotions.vue already handles.
 */
export const PROMO_SKU_REASON_CODES = [
  "sku_not_synced",
  "manual_flash_exists",
  "app_price_already_lower",
  "platform_promotion_skipped",
  "base_price_mismatch",
  "item_level_price_spread",
  "model_not_active",
  "no_promotion_price",
  "promotion_ended",
  "sku_inactive",
  "ownership_columns_missing",
  "duplicate_sku_mapping",
  "remote_expired_window_open"
];

/** `run.status` (§3.3). `running` is the one the page polls on. */
export const RUN_STATUS_RUNNING = "running";
export const RUN_STATUSES = [RUN_STATUS_RUNNING, "done", "failed"];

/** §6: "ระหว่างรัน ปุ่มปิด + poll `runs` ทุก 3 วิ". */
export const PROMOTION_RUNS_POLL_INTERVAL_MS = 3000;

/** The run history the header card reads (endpoint caps `limit` at 50). */
export const PROMOTION_RUN_LIST_LIMIT = 10;

/** The body `code` of the 409 — attach to that run, never show an error (§6). */
export const PROMOTION_IN_FLIGHT_CODE = "promotion_sync_in_flight";

/**
 * `mdfpf_product_sku.flash_source` (§3.1). NULL and '' mean "nobody has claimed
 * this window", which the ownership rule of §4.6 treats exactly like 'manual'.
 */
export const FLASH_SOURCE_SHOPEE = "shopee";
export const FLASH_SOURCE_MANUAL = "manual";

/**
 * The el-tag `type` per status (§6: เขียว / ฟ้า / เทา / เหลือง).
 *
 * `upcoming` is the EMPTY string — the default blue chip. Element UI 2.4.11
 * documents only success / info / warning / danger (src/utils/shopeeBulkSync.js
 * says the same about its own table), so a `type="primary"` would render an
 * unstyled tag; the default chip is this theme's blue.
 */
export const PROMOTION_STATUS_TAG_TYPES = {
  ongoing: "success",
  upcoming: "",
  expired: "info",
  unsupported: "warning"
};

// --------------------------------------------------------------------------
// small normalisers
// --------------------------------------------------------------------------

function stringOf(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return "";
  return String(value).trim();
}

function lower(value) {
  return stringOf(value).toLowerCase();
}

function intOf(value) {
  const n = Number(value);
  if (!isFinite(n) || n <= 0) return 0;
  return Math.floor(n);
}

// --------------------------------------------------------------------------
// the POST body
// --------------------------------------------------------------------------

/**
 * buildSyncBody({shopId, dryRun, selected}) -> the POST /promotions/sync body.
 *
 *   {platform_shop_id:"185922730", dry_run:true}
 *   {platform_shop_id:"185922730", dry_run:false,
 *    promotions:[{promotion_type:"discount", promotion_id:"77"}]}
 *
 * THREE RULES, each of which has cost this repo a bug before:
 *
 *  1. `platform_shop_id` is a STRING. The Go handler binds it into a `string`
 *     field, so a JSON number answers 400 (#580, and the same rule
 *     shopeeBulkSync.bulkStartBody carries). It is NOT routed through Number():
 *     a Shopee shop id is an opaque identifier and a numeric round trip is a
 *     precision risk for nothing. A missing / blank / "0" shop yields "" so the
 *     BE answers its own `shop_required` rather than this screen inventing a
 *     shop 0.
 *
 *  2. `dry_run` DEFAULTS TO TRUE (§5.1). Absent, null and undefined all mean
 *     "report only"; only an explicit false (or the string "false"/0) makes it a
 *     real run. A page that sent `dry_run: undefined` would have the BE default
 *     save it; a page that sent `false` by accident would reprice a catalogue.
 *
 *  3. `promotions` IS OMITTED when nothing is selected, because an EMPTY ARRAY
 *     and an ABSENT key are different instructions to the runner: absent = "the
 *     whole shop", `[]` = "these zero promotions". Sending `[]` for "I ticked
 *     nothing" would turn the button into a no-op that still writes a run row.
 *
 * Selected entries are normalised to {promotion_type, promotion_id} with both
 * as trimmed strings (the id is text everywhere — §2.1, Promotion.ID — because
 * Shopee ids exceed 2^53 and a Number() round trip loses digits). Rows missing
 * either half are dropped: a `{promotion_type:"discount"}` with no id would ask
 * the BE to sync "discount:" and answer 422 at best. A type outside the two
 * known ones is KEPT, not silently dropped — §5.1 answers 422
 * `bad_promotion_type` for it, and swallowing it here would hide a real
 * disagreement between this screen and the BE behind a run that quietly used a
 * different selection than the operator ticked.
 */
export function buildSyncBody(options) {
  const opts = options && typeof options === "object" ? options : {};

  const shop = stringOf(opts.shopId);
  const body = {
    platform_shop_id: shop === "0" ? "" : shop,
    dry_run: normaliseDryRun(opts.dryRun)
  };

  const promotions = normaliseSelection(opts.selected);
  if (promotions.length > 0) body.promotions = promotions;
  return body;
}

/** The §5.1 default: anything that is not explicitly falsey stays a dry run. */
function normaliseDryRun(value) {
  if (value === undefined || value === null) return true;
  if (value === false || value === 0 || value === "false" || value === "0") return false;
  return value !== "" ? Boolean(value) : true;
}

/**
 * normaliseSelection turns whatever the table's checkbox selection holds — the
 * promotion rows themselves, or bare {promotion_type, promotion_id} refs — into
 * the `promotions` array, de-duplicated on the §2.1 key (`type:id`, because
 * Shopee numbers discounts and flash sales in separate id spaces).
 */
export function normaliseSelection(selected) {
  if (!Array.isArray(selected)) return [];
  const out = [];
  const seen = {};
  for (const entry of selected) {
    if (!entry || typeof entry !== "object") continue;
    const type = stringOf(entry.promotion_type || entry.promotionType || entry.type);
    const id = stringOf(entry.promotion_id || entry.promotionId || entry.id);
    if (type === "" || id === "") continue;
    const key = type + ":" + id;
    if (Object.prototype.hasOwnProperty.call(seen, key)) continue;
    seen[key] = true;
    out.push({ promotion_type: type, promotion_id: id });
  }
  return out;
}

/** promotionKey(p) — the row key of the table and of the selection (§2.1). */
export function promotionKey(promotion) {
  if (!promotion || typeof promotion !== "object") return "";
  const type = stringOf(promotion.promotion_type || promotion.promotionType);
  const id = stringOf(promotion.promotion_id || promotion.promotionId);
  if (type === "" && id === "") return "";
  return type + ":" + id;
}

// --------------------------------------------------------------------------
// badges and sentences
// --------------------------------------------------------------------------

/**
 * promotionStatusI18nKey(status) -> `shopee_promotion.status_<status>`.
 *
 * An unknown / absent status gets its own sentence rather than being printed
 * raw: a status this build has not heard of is a BE that grew a state, and
 * "unknown" is a truthful badge where `status_` + garbage is a rendering bug.
 */
export function promotionStatusI18nKey(status) {
  const s = lower(statusValue(status));
  if (PROMOTION_STATUSES.indexOf(s) === -1) return "shopee_promotion.status_unknown";
  return "shopee_promotion.status_" + s;
}

/**
 * promotionStatusBadge(status) -> {type, i18nKey} for one el-tag.
 *
 * Accepts a bare status string OR a whole promotion row, so a caller cannot ask
 * "what badge does this row get?" and silently receive the unknown one.
 */
export function promotionStatusBadge(status) {
  const s = lower(statusValue(status));
  const known = Object.prototype.hasOwnProperty.call(PROMOTION_STATUS_TAG_TYPES, s);
  return {
    type: known ? PROMOTION_STATUS_TAG_TYPES[s] : "info",
    i18nKey: promotionStatusI18nKey(s)
  };
}

function statusValue(value) {
  if (value && typeof value === "object") {
    return value.status !== undefined ? value.status : value.Status;
  }
  return value;
}

/** promotionTypeI18nKey(type) — the "ประเภท" tag (ส่วนลด / Flash Sale). */
export function promotionTypeI18nKey(type) {
  const t = lower(type && typeof type === "object" ? type.promotion_type : type);
  if (PROMOTION_TYPES.indexOf(t) === -1) return "shopee_promotion.type_unknown";
  return "shopee_promotion.type_" + t;
}

/**
 * appStateI18nKey(link) -> the "สถานะในแอป" sentence for one promotion's link.
 *
 *   {state:"synced"}  -> shopee_promotion.app_state_synced   ("sync แล้ว n SKU")
 *   {state:"ended"}   -> shopee_promotion.app_state_ended
 *   {state:"skipped"} -> shopee_promotion.app_state_skipped
 *   {state:"failed"}  -> shopee_promotion.app_state_failed
 *   null / {} / a state this build does not know
 *                     -> shopee_promotion.app_state_none
 *
 * `app_state_none` IS THE COMMON CASE, not an error path: a promotion Shopee
 * reports that this feature has never run over has no link row at all (§3.2),
 * and the operator needs to read "ยังไม่ sync" there — an empty cell reads as a
 * loading bug, and defaulting to `ended` would claim we cleared prices we never
 * wrote.
 *
 * Accepts the promotion row as well as its `link`, since the table has the row.
 */
export function appStateI18nKey(link) {
  const node = link && typeof link === "object" && link.link && typeof link.link === "object"
    ? link.link
    : link;
  if (!node || typeof node !== "object") return "shopee_promotion.app_state_none";
  const state = lower(node.state !== undefined ? node.state : node.State);
  if (APP_STATES.indexOf(state) === -1) return "shopee_promotion.app_state_none";
  return "shopee_promotion.app_state_" + state;
}

/** appStateSkuCount(link) — the `n` of "sync แล้ว n SKU"; 0 when unknown. */
export function appStateSkuCount(link) {
  const node = link && typeof link === "object" && link.link && typeof link.link === "object"
    ? link.link
    : link;
  if (!node || typeof node !== "object") return 0;
  return intOf(node.sku_count !== undefined ? node.sku_count : node.skuCount);
}

/** promoSkuActionI18nKey(action) — the action tag in the per-SKU drawer. */
export function promoSkuActionI18nKey(action) {
  const a = lower(action && typeof action === "object" ? action.action : action);
  if (PROMO_SKU_ACTIONS.indexOf(a) === -1) return "shopee_promotion.action_unknown";
  return "shopee_promotion.action_" + a;
}

/** promotionActionI18nKey(action) — the per-promotion action of one run row. */
export function promotionActionI18nKey(action) {
  const a = lower(action && typeof action === "object" ? action.action : action);
  if (PROMOTION_ACTIONS.indexOf(a) === -1) return "shopee_promotion.promo_action_unknown";
  return "shopee_promotion.promo_action_" + a;
}

/**
 * promoSkuReasonI18nKey(reason_code) -> `shopee_promotion.warn_<code>`.
 *
 * The six codes of §6 plus the five further ones BE-1 emits (see
 * PROMO_SKU_REASON_CODES). Anything else — including '' on a row that simply
 * succeeded — becomes `shopee_promotion.warn_unknown`, which is a real sentence
 * in both dictionaries. Returning '' instead would let the drawer render a blank
 * cell exactly where the operator is looking for the reason a price was skipped.
 *
 * Accepts a bare code, a SKU row, or a warning object ({code, …}).
 */
export function promoSkuReasonI18nKey(reasonCode) {
  let code = reasonCode;
  if (reasonCode && typeof reasonCode === "object") {
    code = reasonCode.reason_code !== undefined
      ? reasonCode.reason_code
      : (reasonCode.reasonCode !== undefined ? reasonCode.reasonCode : reasonCode.code);
  }
  const c = lower(code);
  if (PROMO_SKU_REASON_CODES.indexOf(c) === -1) return "shopee_promotion.warn_unknown";
  return "shopee_promotion.warn_" + c;
}

/**
 * promotionWarnings(data) — the `warnings` array of a list / sync / promotion
 * result, or [].
 *
 * SAME SHAPE AS src/utils/shopeeToSeller.js#toSellerWarnings, deliberately
 * (§6: "ใช้รูปร่าง warning เดิม … ไม่สร้าง component ใหม่"): {code, sku_code,
 * item_id, model_id, message}. One reader, so the drawer renders a promotion
 * warning with the same rows the to-seller preview already uses.
 */
export function promotionWarnings(data) {
  if (!data || typeof data !== "object") return [];
  const list = data.warnings || data.Warnings;
  if (!Array.isArray(list)) return [];
  return list.filter((w) => w && typeof w === "object");
}

// --------------------------------------------------------------------------
// the Bangkok clock
// --------------------------------------------------------------------------

// Shopee sends unix seconds and BE-1 stores timestamptz; §7 #4 forbids any
// local-time parsing on the server and says the conversion to Thai time happens
// HERE. Thailand has been at a fixed +07:00 since 1920 with no DST, so a
// constant is exact and needs no Intl — which, under this repo's Node 16 /
// jsdom, is not guaranteed to carry the tz database (the same reasoning
// src/utils/costReport.js:104 records).
const BANGKOK_OFFSET_MS = 7 * 60 * 60 * 1000;

/** The window separator of §6: "DD/MM HH:mm – DD/MM HH:mm" (en dash). */
export const WINDOW_SEPARATOR = " – ";

function epochOf(value) {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return isFinite(t) ? t : null;
  }
  if (typeof value === "number") {
    if (!isFinite(value) || value <= 0) return null;
    // Unix SECONDS (what Shopee speaks) rather than milliseconds: a value under
    // 1e12 is before 2001 in ms and is always a seconds timestamp in practice.
    return value < 1e12 ? value * 1000 : value;
  }
  const s = stringOf(value);
  if (s === "") return null;
  // Go's zero time, and MariaDB's, must read as "no date" rather than as 1 Jan
  // year 1 (the fix-wms-date-literal-z lesson).
  if (s.indexOf("0001-01-01") === 0 || s.indexOf("0000-00-00") === 0) return null;
  const t = Date.parse(s);
  return isFinite(t) ? t : null;
}

function bangkokFields(ms) {
  const d = new Date(ms + BANGKOK_OFFSET_MS);
  const pad = (n) => (n < 10 ? "0" + n : String(n));
  return {
    day: pad(d.getUTCDate()),
    month: pad(d.getUTCMonth() + 1),
    year: String(d.getUTCFullYear()),
    hour: pad(d.getUTCHours()),
    minute: pad(d.getUTCMinutes())
  };
}

/**
 * formatInstant(value) -> "DD/MM HH:mm" in Asia/Bangkok, or '' when the value is
 * not a usable instant.
 *
 * The UTC getters are applied to a shifted epoch rather than the local ones to a
 * real Date: reading getHours() would print the BROWSER's clock, so an operator
 * on a laptop still set to Europe would read a promotion window seven hours off
 * — the exact shape of the bugs fix-wms-date-literal-z collects.
 */
export function formatInstant(value) {
  const ms = epochOf(value);
  if (ms === null) return "";
  const f = bangkokFields(ms);
  return f.day + "/" + f.month + " " + f.hour + ":" + f.minute;
}

/** formatDateTime(value) -> "DD/MM/YYYY HH:mm" in Bangkok, for the "รันล่าสุด" line. */
export function formatDateTime(value) {
  const ms = epochOf(value);
  if (ms === null) return "";
  const f = bangkokFields(ms);
  return f.day + "/" + f.month + "/" + f.year + " " + f.hour + ":" + f.minute;
}

/**
 * formatWindow(starts_at, ends_at) -> "DD/MM HH:mm – DD/MM HH:mm" (§6).
 *
 * One side unusable -> the other side ALONE, with no separator: a dangling
 * "12/10 09:00 –" reads as a promotion with no end, which is a different claim
 * from "we do not know the end". Neither usable -> '' so the cell can fall back
 * to a dash of its own.
 */
export function formatWindow(startsAt, endsAt) {
  const from = formatInstant(startsAt);
  const to = formatInstant(endsAt);
  if (from === "" && to === "") return "";
  if (from === "") return to;
  if (to === "") return from;
  return from + WINDOW_SEPARATOR + to;
}

// --------------------------------------------------------------------------
// errors
// --------------------------------------------------------------------------

/** errorBody digs the payload out, tolerating the {data:{…}} envelope. */
function errorBody(error) {
  const data = error && error.response ? error.response.data : error;
  if (!data || typeof data !== "object") return null;
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    const inner = data.data;
    if (inner.code !== undefined || inner.run_id !== undefined || inner.Code !== undefined) {
      return inner;
    }
  }
  return data;
}

function bodyCode(data) {
  if (!data || typeof data !== "object") return "";
  return stringOf(data.code || data.Code || data.error_code || data.errorCode);
}

/** The error codes of §5.1 mapped to the sentence the page shows. */
const ERROR_CODE_KEYS = {
  shop_required: "shopee_promotion.err_shop_required",
  shop_not_found: "shopee_promotion.err_shop_not_found",
  promotion_sync_in_flight: "shopee_promotion.err_in_flight",
  bad_promotion_type: "shopee_promotion.err_bad_promotion_type",
  // §10.5: the shop is real and the caller owns it — it is simply not the one
  // shop this feature mirrors. Its own sentence, never the generic one, because
  // the fix is "switch to the master shop", not "try again".
  shop_not_master: "shopee_promotion.err_shop_not_master",
  promotion_fetch_failed: "shopee_promotion.err_fetch_failed",
  promotion_sync_disabled: "shopee_promotion.err_disabled",
  promotion_sync_unavailable: "shopee_promotion.err_generic"
};

/** Fallback by HTTP status, for a coded answer this build does not know. */
const ERROR_STATUS_KEYS = {
  400: "shopee_promotion.err_shop_required",
  404: "shopee_promotion.err_shop_not_found",
  409: "shopee_promotion.err_in_flight",
  422: "shopee_promotion.err_bad_promotion_type",
  502: "shopee_promotion.err_fetch_failed",
  503: "shopee_promotion.err_disabled"
};

/**
 * promotionErrorI18nKey(status, code) -> the sentence for one failure.
 *
 * Both arguments are optional and either may be handed the axios error itself:
 *   promotionErrorI18nKey(err)
 *   promotionErrorI18nKey(422, "shop_not_master")
 *   promotionErrorI18nKey(0, "promotion_sync_disabled")
 *
 * THE CODE WINS OVER THE STATUS. A proxy can turn a 502 into a 504 and a
 * gateway can answer 409 of its own; the body's `code` is this feature's own
 * vocabulary and is the only thing worth trusting when both are present.
 * Everything unrecognised lands on `err_generic`, which is a real sentence —
 * never an empty string and never the raw code.
 */
export function promotionErrorI18nKey(status, code) {
  let httpStatus = 0;
  let bodyCodeValue = "";

  if (status && typeof status === "object") {
    const err = status;
    httpStatus = err.response ? Number(err.response.status) : 0;
    bodyCodeValue = bodyCode(errorBody(err));
  } else {
    const n = Number(status);
    httpStatus = isFinite(n) ? n : 0;
  }

  if (code !== undefined && code !== null) {
    if (typeof code === "object") {
      bodyCodeValue = bodyCode(errorBody(code)) || bodyCodeValue;
    } else if (stringOf(code) !== "") {
      bodyCodeValue = stringOf(code);
    }
  }

  const c = bodyCodeValue.toLowerCase();
  if (Object.prototype.hasOwnProperty.call(ERROR_CODE_KEYS, c)) return ERROR_CODE_KEYS[c];
  if (Object.prototype.hasOwnProperty.call(ERROR_STATUS_KEYS, httpStatus)) {
    return ERROR_STATUS_KEYS[httpStatus];
  }
  return "shopee_promotion.err_generic";
}

/** promotionErrorCode(error) — the body `code`, '' when there is none. */
export function promotionErrorCode(error) {
  return bodyCode(errorBody(error));
}

/** promotionErrorDetail(error) — the BE's own text under the translated sentence. */
export function promotionErrorDetail(error) {
  const data = errorBody(error);
  if (!data || typeof data !== "object") return "";
  return stringOf(data.message || data.Message || data.Msg || data.msg);
}

/**
 * promotionInFlightRunId(error) — the id of the run that is already going, or
 * null.
 *
 * null, not 0: "there is no such run" is a different statement from "run 0", and
 * the caller reads better for it (`if (runId !== null) attach(runId)`). Both the
 * 409 STATUS and the `promotion_sync_in_flight` CODE must match — a 409 from a
 * proxy, or any other coded 409 the route might grow, must not be mistaken for
 * "attach to this run". Same move as shopeeBulkSync.bulkInFlightJobId (§6).
 */
export function promotionInFlightRunId(error) {
  const status = error && error.response ? Number(error.response.status) : 0;
  if (status !== 409) return null;
  const data = errorBody(error);
  if (bodyCode(data) !== PROMOTION_IN_FLIGHT_CODE) return null;
  const raw = data.run_id !== undefined ? data.run_id : data.RunID;
  const id = intOf(raw);
  return id > 0 ? id : null;
}

/**
 * isPromotionDisabled(source) — is the feature turned off?
 *
 * TRUE for a 503 `promotion_sync_disabled` error AND for a 200 `runs` body that
 * says `enabled:false` — the two ways §9 step 2 can present the same fact. The
 * page shows one "ปิดอยู่" panel for both, so it must not have to ask twice.
 *
 * A body with NO `enabled` key is NOT disabled: an older API that predates this
 * feature, or one whose handler omits the flag, must not black out the page.
 */
export function isPromotionDisabled(source) {
  if (!source || typeof source !== "object") return false;
  if (source.response) {
    const status = Number(source.response.status);
    if (status === 503) return true;
    return promotionErrorCode(source) === "promotion_sync_disabled";
  }
  const body = source.data && typeof source.data === "object" ? source.data : source;
  if (Object.prototype.hasOwnProperty.call(body, "enabled")) return body.enabled === false;
  if (Object.prototype.hasOwnProperty.call(body, "Enabled")) return body.Enabled === false;
  return false;
}

/**
 * promoShowUpcoming(payload) — the §10.3 setting as the API reports it.
 *
 * BOTH SPELLINGS ARE ACCEPTED. §10.3 names the setting key
 * `promo_show_upcoming`; BE-1's handlers carry it on the wire as
 * `show_upcoming` (it is already inside a promotions payload, so the prefix is
 * redundant there). A reader that knew only one of the two would silently show
 * the switch as off against half the builds.
 */
export function promoShowUpcoming(payload) {
  if (!payload || typeof payload !== "object") return false;
  const body = payload.data && typeof payload.data === "object" ? payload.data : payload;
  const raw = [
    body.promo_show_upcoming,
    body.promoShowUpcoming,
    body.show_upcoming,
    body.showUpcoming
  ].find((v) => v !== undefined && v !== null);
  if (raw === undefined) return false;
  return raw === true || raw === 1 || raw === "1" || raw === "true";
}

// --------------------------------------------------------------------------
// runs
// --------------------------------------------------------------------------

/**
 * summarizeRun(run) -> the "รันล่าสุด: …" line and the poll's stop condition:
 *
 *   {runId, status, trigger, dryRun, running, finished, failed,
 *    at, startedAt, finishedAt,
 *    promotionsTotal, promotionsApplied, skusSet, skusCleared, skusSkipped,
 *    error}
 *
 * `at` is the FINISHED time when there is one and the started time otherwise, in
 * Bangkok — the operator's question is "when did this last happen", and a
 * running row has not happened yet.
 *
 * `running` is what the page polls on, and it is read off the STATUS rather than
 * off a missing `finished_at`: a run killed mid-flight keeps `status='running'`
 * until the lease expires (§3.3), and treating "no finish time" as "done" would
 * re-enable the buttons while the runner still holds the busy guard.
 *
 * A null / garbage run summarises to zeros with runId 0 and status '' rather
 * than throwing — the header renders `last_run_never` off exactly that.
 */
export function summarizeRun(run) {
  const r = run && typeof run === "object" ? run : {};
  const status = lower(r.status !== undefined ? r.status : r.Status);
  const startedAt = r.started_at !== undefined ? r.started_at : r.startedAt;
  const finishedAt = r.finished_at !== undefined ? r.finished_at : r.finishedAt;
  const at = formatDateTime(finishedAt) || formatDateTime(startedAt);
  return {
    runId: intOf(r.id !== undefined ? r.id : (r.run_id !== undefined ? r.run_id : r.runId)),
    status: status,
    trigger: lower(r.trigger),
    dryRun: r.dry_run === true || r.dryRun === true,
    running: status === RUN_STATUS_RUNNING,
    finished: status === "done" || status === "failed",
    failed: status === "failed",
    at: at,
    startedAt: stringOf(startedAt),
    finishedAt: stringOf(finishedAt),
    promotionsTotal: intOf(r.promotions_total !== undefined ? r.promotions_total : r.promotionsTotal),
    promotionsApplied: intOf(
      r.promotions_applied !== undefined ? r.promotions_applied : r.promotionsApplied
    ),
    skusSet: intOf(r.skus_set !== undefined ? r.skus_set : r.skusSet),
    skusCleared: intOf(r.skus_cleared !== undefined ? r.skus_cleared : r.skusCleared),
    skusSkipped: intOf(r.skus_skipped !== undefined ? r.skus_skipped : r.skusSkipped),
    error: stringOf(r.last_error !== undefined ? r.last_error : r.lastError)
  };
}

/** latestRun(body) — the newest run of a `runs` response ({runs:[…]} newest first). */
export function latestRun(body) {
  const list = runList(body);
  return list.length ? list[0] : null;
}

/** runList(body) — the `runs` array, through however many envelopes. */
export function runList(body) {
  if (Array.isArray(body)) return body.filter((r) => r && typeof r === "object");
  if (!body || typeof body !== "object") return [];
  const node = body.data && typeof body.data === "object" ? body.data : body;
  const list = node.runs || node.Runs;
  return Array.isArray(list) ? list.filter((r) => r && typeof r === "object") : [];
}

/**
 * inFlightRunId(body) — the run the `runs` response says is still going, or null.
 *
 * BE-1 answers `in_flight` directly; a body without it falls back to scanning
 * the rows for `status='running'`, so the page keeps working against a handler
 * that has not got the field yet.
 */
export function inFlightRunId(body) {
  if (!body || typeof body !== "object") return null;
  const node = body.data && typeof body.data === "object" ? body.data : body;
  const direct = intOf(node.in_flight !== undefined ? node.in_flight : node.inFlight);
  if (direct > 0) return direct;
  const rows = runList(body);
  for (const row of rows) {
    const summary = summarizeRun(row);
    if (summary.running && summary.runId > 0) return summary.runId;
  }
  return null;
}

/** runsParams(shopId, limit) — the GET /promotions/runs query (limit capped at 50). */
export function runsParams(shopId, limit) {
  const shop = stringOf(shopId);
  const n = Number(limit);
  const params = {
    limit: isFinite(n) && n > 0 ? Math.min(Math.floor(n), 50) : PROMOTION_RUN_LIST_LIMIT
  };
  if (shop !== "" && shop !== "0") params.platform_shop_id = shop;
  return params;
}

/**
 * listParams(shopId, {type, status, page, pageSize}) — the GET /promotions query.
 *
 * `type` / `status` are sent only when they are a value §5.1 accepts, and "all"
 * is sent explicitly rather than omitted because that is the documented default
 * and a visible one reads better in a network log. An unknown filter is dropped
 * rather than forwarded: unlike a promotion TYPE in the sync body (which must
 * reach the BE so the disagreement surfaces as 422), a bad query filter would
 * blank the whole table for a typo in a select the operator cannot see.
 */
export function listParams(shopId, options) {
  const opts = options && typeof options === "object" ? options : {};
  const shop = stringOf(shopId);
  const params = { platform_shop_id: shop === "0" ? "" : shop };

  const type = lower(opts.type);
  if (type === "all" || PROMOTION_TYPES.indexOf(type) !== -1) params.type = type;

  const status = lower(opts.status);
  const statusFilters = ["all", PROMOTION_STATUS_UPCOMING, PROMOTION_STATUS_ONGOING, PROMOTION_STATUS_EXPIRED];
  if (statusFilters.indexOf(status) !== -1) params.status = status;

  const page = Number(opts.page);
  if (isFinite(page) && page > 1) params.page = Math.floor(page);
  const pageSize = Number(opts.pageSize);
  if (isFinite(pageSize) && pageSize > 0) params.page_size = Math.min(Math.floor(pageSize), 100);
  return params;
}

// --------------------------------------------------------------------------
// §7.7 — who owns a SKU's flash window
// --------------------------------------------------------------------------

/**
 * flashSourceOf(sku) — the normalised `flash_source`, '' when the API sent none.
 *
 * Lower-cased and trimmed: the value is a wire enum, and a build that left the
 * inputs editable for "Shopee" while the next sync overwrote them is the exact
 * bug §9 step 5 requires this screen to land before `mode: write`.
 */
export function flashSourceOf(sku) {
  if (!sku || typeof sku !== "object") return "";
  const raw = sku.flash_source !== undefined ? sku.flash_source : sku.flashSource;
  return lower(raw);
}

/**
 * isShopeeFlashSku(sku) — is this SKU's flash window owned by the Shopee mirror?
 *
 * THE GATE IS `flash_source`, NOT "has a promotion link" (§6, and the same
 * reasoning stockSource.js records for `stock_source`): a SKU can carry
 * `flash_promotion_link_id` from a promotion that has ended, in which case the
 * window is free again and staff must be able to set one. A SKU with NO
 * `flash_source` at all behaves exactly as it did before this feature — that is
 * the rollback path (§9 step 7 clears the column), not politeness.
 */
export function isShopeeFlashSku(sku) {
  return flashSourceOf(sku) === FLASH_SOURCE_SHOPEE;
}

/**
 * flashSourceForSave(sku) — the `flash_source` one SKU's save payload must
 * carry, or '' when the key must be omitted.
 *
 *   a Shopee-owned SKU            -> 'shopee'   (echoed back untouched)
 *   a SKU with any flash value    -> 'manual'   (§7.7: staff claims the window)
 *   a SKU with no flash at all    -> ''         (nothing to own)
 *
 * WHY 'manual' MATTERS. §4.6 skips a SKU whose `flash_price` is set with a
 * `flash_source` of NULL/'manual' and warns `manual_flash_exists`; a SKU whose
 * source still says 'shopee' is overwritten by the next ticker without a word
 * (§7 #7). So the moment staff types a flash price by hand, the save MUST say
 * 'manual' or their number lives for at most ten minutes.
 *
 * The Shopee case echoes 'shopee' rather than omitting the key so that the
 * payload states the ownership it read — BE-3 ignores the flash VALUES of such a
 * SKU anyway (the gate is on its side too), and a save that silently dropped the
 * source would let a BE that upserts the column blank it.
 */
export function flashSourceForSave(sku) {
  if (!sku || typeof sku !== "object") return "";
  if (isShopeeFlashSku(sku)) return FLASH_SOURCE_SHOPEE;
  return hasFlashValue(sku) ? FLASH_SOURCE_MANUAL : "";
}

/**
 * hasFlashValue(sku) — does this row carry a flash price or a window?
 *
 * Reads the FORM's shape as well as the API's: ProductEdit binds the window to a
 * single `flashRange` array for el-date-picker, so a reader that only knew
 * `flash_starts_at` would call a row with a freshly picked window "empty" and
 * omit the 'manual' claim that protects it.
 */
export function hasFlashValue(sku) {
  if (!sku || typeof sku !== "object") return false;
  const price = sku.flash_price !== undefined ? sku.flash_price : sku.flashPrice;
  if (price !== null && price !== undefined && price !== "" && Number(price) > 0) return true;
  const range = sku.flashRange;
  if (Array.isArray(range) && (range[0] || range[1])) return true;
  const starts = sku.flash_starts_at !== undefined ? sku.flash_starts_at : sku.flashStartsAt;
  const ends = sku.flash_ends_at !== undefined ? sku.flash_ends_at : sku.flashEndsAt;
  return Boolean(stringOf(starts) || stringOf(ends));
}

/**
 * applyFlashSource(payloadSkus, skus) — stamp `flash_source` onto the save
 * payload of /app-catalog/products/:id/edit.
 *
 * Returns a NEW array; neither argument is mutated (the form rows are reactive
 * Vue state and the payload is built from them one line earlier) — the same
 * contract stripWmsQuantities keeps.
 *
 * ALIGNMENT is positional when the two arrays are the same length, which is the
 * real case since the payload is `form.skus.map(...)`; otherwise rows are
 * matched on `sku_code`. Positional wins where it applies because two SKUs can
 * share a placeholder code before the WMS dialog has run, and a code lookup
 * would then stamp the wrong row.
 */
export function applyFlashSource(payloadSkus, skus) {
  if (!Array.isArray(payloadSkus)) return [];
  const rows = Array.isArray(skus) ? skus : [];
  const aligned = rows.length === payloadSkus.length;

  const byCode = {};
  rows.forEach((row) => {
    const code = stringOf(row && row.sku_code);
    if (code !== "" && !Object.prototype.hasOwnProperty.call(byCode, code)) byCode[code] = row;
  });

  return payloadSkus.map((entry, i) => {
    if (!entry || typeof entry !== "object") return entry;
    const row = aligned ? rows[i] : byCode[stringOf(entry.sku_code)];
    const source = flashSourceForSave(row !== undefined ? row : entry);
    if (source === "") return entry;
    return Object.assign({}, entry, { flash_source: source });
  });
}

export default {
  PROMOTION_TYPES,
  PROMOTION_STATUSES,
  APP_STATES,
  PROMO_SKU_ACTIONS,
  PROMO_SKU_REASON_CODES,
  PROMOTION_RUNS_POLL_INTERVAL_MS,
  PROMOTION_IN_FLIGHT_CODE,
  FLASH_SOURCE_SHOPEE,
  FLASH_SOURCE_MANUAL,
  buildSyncBody,
  normaliseSelection,
  promotionKey,
  promotionStatusBadge,
  promotionStatusI18nKey,
  promotionTypeI18nKey,
  appStateI18nKey,
  appStateSkuCount,
  promoSkuActionI18nKey,
  promotionActionI18nKey,
  promoSkuReasonI18nKey,
  promotionWarnings,
  formatInstant,
  formatDateTime,
  formatWindow,
  promotionErrorI18nKey,
  promotionErrorCode,
  promotionErrorDetail,
  promotionInFlightRunId,
  isPromotionDisabled,
  promoShowUpcoming,
  summarizeRun,
  latestRun,
  runList,
  inFlightRunId,
  runsParams,
  listParams,
  flashSourceOf,
  isShopeeFlashSku,
  flashSourceForSave,
  hasFlashValue,
  applyFlashSource
};
